import type { Answer, IReaction } from 'types/answers'

type SignalQuestSettingsSnapshot = {
    id?: string | null
    private?: boolean | null
    team_id?: string | number | null
    upvalues_enabled?: boolean
    free_reactions_enabled?: boolean
}

// Keep the paid-only rollout behind one gate so reintroducing free-first behavior
// does not require threading a user preference back through every signal surface.
export const onlyPaidUpvalueEnabled = (): boolean => true

export const sameUserId = (
    left: string | number | null | undefined,
    right: string | number | null | undefined,
) => left != null && right != null && String(left) === String(right)

export const freeReactionsEnabledForSignalQuestSnapshot = (
    quest: SignalQuestSettingsSnapshot | null | undefined,
): boolean | undefined => {
    if (!quest) return undefined

    return quest.private === true && !quest.team_id && quest.free_reactions_enabled !== false
}

export const upvaluesEnabledForSignalQuestSnapshot = (
    quest: SignalQuestSettingsSnapshot | null | undefined,
): boolean | undefined => {
    if (!quest) return undefined

    return quest.upvalues_enabled !== false
}

export const answerWithSignalQuestSettings = (
    answer: Answer,
    quest: SignalQuestSettingsSnapshot | null | undefined,
): Answer => {
    if (!quest) return answer

    const signalQuestId = quest.id ?? answer.signal_quest_id ?? null
    const upvaluesEnabled = upvaluesEnabledForSignalQuestSnapshot(quest)
    const freeReactionsEnabled = freeReactionsEnabledForSignalQuestSnapshot(quest)

    if (
        answer.signal_quest_id === signalQuestId &&
        answer.upvalues_enabled === upvaluesEnabled &&
        answer.free_reactions_enabled === freeReactionsEnabled
    ) {
        return answer
    }

    return {
        ...answer,
        signal_quest_id: signalQuestId,
        upvalues_enabled: upvaluesEnabled,
        free_reactions_enabled: freeReactionsEnabled,
    }
}

export const answerWithActiveSignalSettings = (answer: Answer, activeAnswer: Answer): Answer => {
    const signalQuestId =
        activeAnswer.signal_quest_id !== undefined
            ? activeAnswer.signal_quest_id
            : answer.signal_quest_id
    const upvaluesEnabled =
        activeAnswer.upvalues_enabled !== undefined
            ? activeAnswer.upvalues_enabled
            : answer.upvalues_enabled
    const freeReactionsEnabled =
        activeAnswer.free_reactions_enabled !== undefined
            ? activeAnswer.free_reactions_enabled
            : answer.free_reactions_enabled

    if (
        answer.signal_quest_id === signalQuestId &&
        answer.upvalues_enabled === upvaluesEnabled &&
        answer.free_reactions_enabled === freeReactionsEnabled
    ) {
        return answer
    }

    return {
        ...answer,
        signal_quest_id: signalQuestId,
        upvalues_enabled: upvaluesEnabled,
        free_reactions_enabled: freeReactionsEnabled,
    }
}

export const currentUserReactionForAnswer = (
    answer: Answer,
    currentUserId: string | number | null | undefined,
): IReaction | undefined => {
    if (currentUserId == null) return undefined
    return (answer.reactions || []).find(reaction => sameUserId(reaction.user_id, currentUserId))
}

export const currentUserHasUpvaluedAnswer = (
    answer: Answer,
    currentUserId: string | number | null | undefined,
): boolean => {
    if (currentUserId == null) return false
    return (answer.bsv_upvalues || []).some(upvalue =>
        sameUserId(upvalue.from_user_id, currentUserId),
    )
}

export const answerCanAcceptFreeReactionFromUser = (
    answer: Answer,
    currentUserId: string | number | null | undefined,
): boolean => {
    if (currentUserId == null) return false

    if (answer.free_reactions_enabled === false) return false
    if (currentUserHasUpvaluedAnswer(answer, currentUserId)) return false

    return true
}

export const nextPressIsFreeReactionForAnswer = (
    answer: Answer,
    currentUserId: string | number | null | undefined,
): boolean => {
    if (!answerCanAcceptFreeReactionFromUser(answer, currentUserId)) return false

    return (
        !currentUserReactionForAnswer(answer, currentUserId) &&
        !currentUserHasUpvaluedAnswer(answer, currentUserId)
    )
}

export const canSendFreeReactionForAnswerSnapshot = (
    answer: Answer,
    currentUserId: string | number | null | undefined,
): boolean => {
    if (!answerCanAcceptFreeReactionFromUser(answer, currentUserId)) return false

    const existingReaction = currentUserReactionForAnswer(answer, currentUserId)
    return !existingReaction || answer.upvalues_enabled === false
}
